// 2026-10-10 — Morning Reversal (PAPER ONLY). Backend: morning_reversal.py,
// spec docs/MORNING_REVERSAL_PAPER_SPEC.md. Top-15 F&O decliners at 09:30,
// 3-min EMA9 touch -> body range -> close above by 10:30; ATM call bought at
// the ask, sold at the bid. Primary portfolio capped at 3/day; all other
// qualifying signals are logged and flagged.
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Download, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const pct = (v) => (v == null ? "—" : `${v >= 0 ? "+" : ""}${Number(v).toFixed(2)}%`);
const col = (v) => (v == null ? undefined : v >= 0 ? "var(--bull)" : "var(--bear)");
const STATUS = {
  new: "waiting for 09:30 candles", watching: "watching", trend_fail: "no trend open", no_touch: "no EMA touch",
  zero_range: "zero range", continuation_break: "broke down", expired: "no break by 10:30", open: "OPEN",
  closed: "closed", quote_rejected: "quote rejected", stale_signal: "signal too late",
};

const Row = ({ label, s }) => (
  <tr className="border-b border-grid/40">
    <td className="px-2 py-1 text-muted-2">{label}</td>
    <td className="px-2 py-1 text-right">{s?.trades ?? 0}</td>
    <td className="px-2 py-1 text-right">{s?.win_rate_pct == null ? "—" : `${s.win_rate_pct}%`}</td>
    <td className="px-2 py-1 text-right" style={{ color: col(s?.avg_pct) }}>{pct(s?.avg_pct)}</td>
    <td className="px-2 py-1 text-right">{s?.profit_factor ?? "—"}</td>
    <td className="px-2 py-1 text-right text-muted-2">{s?.ltp_based?.profit_factor ?? "—"}</td>
    <td className="px-2 py-1 text-right text-muted-2">{s?.r1?.profit_factor ?? "—"}</td>
    <td className="px-2 py-1 text-right text-muted-2">{s?.avg_spread_cost_pct == null ? "—" : `${s.avg_spread_cost_pct}%`}</td>
    <td className="px-2 py-1 text-right text-muted-2">{s?.fakeout_pct == null ? "—" : `${s.fakeout_pct}%`}</td>
  </tr>
);

export const MorningReversal = () => {
  const [today, setToday] = useState(null);
  const [sum, setSum] = useState(null);
  const load = useCallback(async () => {
    try {
      const [t, s] = await Promise.all([
        axios.get(`${API}/morning-reversal/today`),
        axios.get(`${API}/morning-reversal/summary`, { params: { days: 30 } }),
      ]);
      setToday(t.data);
      setSum(s.data);
    } catch (e) { /* card stays hidden on error */ }
  }, []);
  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);
  if (!today && !sum) return null;
  const cands = today?.candidates || [];
  const p = sum?.primary || {};
  return (
    <section className="border-grid surface px-5 py-4 space-y-3" data-testid="morning-reversal-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-3 font-mono-data">Morning Reversal · paper only</div>
          <div className="text-[11px] text-muted-2 font-mono-data mt-1">
            Top 15 F&amp;O decliners at 09:30 → first 3-min EMA9 touch → 2-candle body range (0.6–1.0× ATR) → close above it by
            10:30. ATM call bought at the <b>ask</b>, sold at the <b>bid</b>. Stop range low · 2R target (1R recorded) · flat 11:00.
            Primary portfolio max 3/day; every other signal is still logged. Decide after 30 primary trades (PF ≥ 1.3, avg ≥ +2%).
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={load} className="p-1.5 border border-grid text-muted-2" title="Refresh"><RefreshCw size={12} /></button>
          <a href={`${API}/morning-reversal/export.csv?days=90`} className="p-1.5 border border-grid text-muted-2" title="Export CSV"><Download size={12} /></a>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-[11px] font-mono-data">
          <thead className="text-muted-3 text-[9px] uppercase tracking-[0.15em]">
            <tr className="border-b border-grid">
              {["Last 30 days", "Trades", "Win", "Avg (ask→bid)", "PF", "PF (LTP)", "PF 1R", "Spread cost", "Fake-outs"].map((h, i) => (
                <th key={h} className={`${i ? "text-right" : "text-left"} px-2 py-1 font-normal`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <Row label={`Primary (${p.per_day ?? 0}/day)`} s={p} />
            <Row label="All signals" s={sum?.all_signals} />
            <Row label="Band pass" s={sum?.band_pass} />
            <Row label="Band fail (logged)" s={sum?.band_fail} />
            <Row label="Over cap (logged)" s={sum?.over_cap} />
            <Row label="Nifty 100 (band pass)" s={sum?.nifty100} />
            <Row label="Other F&O (band pass)" s={sum?.other_fno} />
          </tbody>
        </table>
      </div>

      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">
        Today {today?.day} · {cands.length ? `${cands.length} decliners` : "candidates appear at 09:30"}
      </div>
      {cands.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px] font-mono-data">
            <thead className="text-muted-3 text-[9px] uppercase tracking-[0.15em]">
              <tr className="border-b border-grid">
                {["#", "Symbol", "Chg 09:27", "Status", "Range/ATR", "Signal", "Strike", "Ask in", "Bid out", "P&L", "Exit", "Book"].map((h) => (
                  <th key={h} className="text-left px-2 py-1 font-normal">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cands.map((c) => (
                <tr key={c.symbol} className="border-b border-grid/40">
                  <td className="px-2 py-1 text-muted-3">{c.rank}</td>
                  <td className="px-2 py-1 font-semibold">{c.symbol}{c.nifty100 ? "" : <span className="text-muted-3"> ·F&amp;O</span>}</td>
                  <td className="px-2 py-1" style={{ color: col(c.chg_pct_0927) }}>{pct(c.chg_pct_0927)}</td>
                  <td className="px-2 py-1 text-muted-2">{STATUS[c.status] || c.status}{c.reject_reason ? ` (${c.reject_reason})` : ""}</td>
                  <td className="px-2 py-1">{c.range_ratio == null ? "—" : `${c.range_ratio.toFixed(2)}${c.band_fail ? " ✗" : ""}`}</td>
                  <td className="px-2 py-1">{c.signal_t || "—"}</td>
                  <td className="px-2 py-1">{c.strike ? `${c.strike} CE` : "—"}</td>
                  <td className="px-2 py-1">{c.entry ?? "—"}</td>
                  <td className="px-2 py-1">{c.exit ?? "—"}</td>
                  <td className="px-2 py-1" style={{ color: col(c.pnl_pct) }}>{pct(c.pnl_pct)}</td>
                  <td className="px-2 py-1 text-muted-2">{c.exit_reason || "—"}</td>
                  <td className="px-2 py-1 text-muted-2">{c.primary ? "primary" : c.over_cap ? "over cap" : c.band_fail && c.entry ? "band fail" : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default MorningReversal;
